/**
 * ============================================================================
 * ADMIN PAYMENT DETAIL PAGE - 100% Ready
 * ============================================================================
 * 
 * Admin page for viewing and managing a single payment
 * Uses componentShowcase patterns and styles
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAuthContext } from '../../providers';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import Modal from '../../components/common/Modal';
import Textarea from '../../components/common/Textarea';
import '../../styles/animations.css';
import '../../styles/global.css';

/**
 * Admin Payment Detail Component
 * @returns {JSX.Element}
 */
const AdminPaymentDetail = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated } = useAuthContext();
  const { getPaymentDetails, retryPayment, refundPayment, loading } = useAdmin();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [payment, setPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [retryData, setRetryData] = useState({});
  const [refundData, setRefundData] = useState({
    reason: '',
    notes: '',
  });

  // Check if user is admin
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (authUser?.role !== 'admin' && authUser?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
  }, [isAuthenticated, authUser, navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const response = await getPaymentDetails(paymentId);
        setPayment(response?.payment || response?.data);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement du paiement' : 'Failed to load payment')
        );
        console.error('Load payment error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (paymentId && isAuthenticated && (authUser?.role === 'admin' || authUser?.role === 'super_admin')) {
      loadData();
    }
  }, [paymentId, isAuthenticated, authUser, getPaymentDetails, showError, language]);

  const handleRetry = async () => {
    if (!payment) return;

    try {
      await retryPayment(paymentId, retryData);
      showSuccess(language === 'fr' ? 'Paiement relancé!' : 'Payment retried!');
      setShowRetryModal(false);
      setRetryData({});
      // Reload payment
      const response = await getPaymentDetails(paymentId);
      setPayment(response?.payment || response?.data);
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec de la relance' : 'Failed to retry'));
    }
  };

  const handleRefund = async () => {
    if (!payment) return;

    try {
      if (!refundData.reason || refundData.reason.length < 10) {
        showError(
          language === 'fr'
            ? 'La raison du remboursement doit contenir au moins 10 caractères'
            : 'Refund reason must be at least 10 characters'
        );
        return;
      }

      await refundPayment(paymentId, refundData);
      showSuccess(language === 'fr' ? 'Remboursement effectué!' : 'Refund completed!');
      setShowRefundModal(false);
      setRefundData({ reason: '', notes: '' });
      // Reload payment
      const response = await getPaymentDetails(paymentId);
      setPayment(response?.payment || response?.data);
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec du remboursement' : 'Failed to refund'));
    }
  };

  const getStatusVariant = (status) => {
    const variants = {
      pending: 'warning',
      processing: 'primary',
      completed: 'success',
      failed: 'error',
      cancelled: 'default',
      refunded: 'info',
    };
    return variants[status] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'from-yellow-500 to-orange-500',
      processing: 'from-blue-500 to-cyan-500',
      completed: 'from-green-500 to-emerald-500',
      failed: 'from-red-500 to-pink-500',
      cancelled: 'from-gray-500 to-slate-500',
      refunded: 'from-purple-500 to-pink-500',
    };
    return colors[status] || 'from-gray-500 to-slate-500';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      processing: '🔄',
      completed: '✅',
      failed: '❌',
      cancelled: '🚫',
      refunded: '↩️',
    };
    return icons[status] || '💳';
  };

  const getPaymentIcon = (method) => {
    const icons = {
      card: '💳',
      mtn_mobile_money: '📱',
      orange_money: '🍊',
      mpesa: '📱',
      bank_transfer: '🏦',
    };
    return icons[method] || '💰';
  };

  const getPaymentMethodName = (method) => {
    const names = {
      card: language === 'fr' ? 'Carte bancaire' : 'Card',
      mtn_mobile_money: 'MTN Mobile Money',
      orange_money: 'Orange Money',
      mpesa: 'M-Pesa',
      bank_transfer: language === 'fr' ? 'Virement bancaire' : 'Bank Transfer',
    };
    return names[method] || method;
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (!payment) {
    return (
      <AdminLayout>
        <Card variant="glass" className="p-12 backdrop-blur-xl">
          <EmptyState
            icon="💳"
            title={language === 'fr' ? 'Paiement introuvable' : 'Payment not found'}
            description={
              language === 'fr'
                ? 'Le paiement demandé n\'existe pas ou a été supprimé'
                : 'The requested payment does not exist or has been deleted'
            }
          />
        </Card>
      </AdminLayout>
    );
  }

  const canRetry = payment.status === 'failed';
  const canRefund = payment.status === 'completed';

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/payments')}
          className="mb-4"
        >
          ← {language === 'fr' ? 'Retour aux paiements' : 'Back to Payments'}
        </Button>
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
          💳 {language === 'fr' ? 'Détails du Paiement' : 'Payment Details'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Gérez et surveillez ce paiement'
            : 'Manage and monitor this payment'}
        </p>
      </div>

      {/* Payment Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <Card variant="glass" className="backdrop-blur-xl border-white/20 dark:border-white/10">
            <div className="space-y-6">
              {/* Status Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${getStatusColor(payment.status)} flex items-center justify-center text-4xl shadow-2xl shadow-blue-500/30 animate-float`}>
                    {getPaymentIcon(payment.paymentMethod || payment.method)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-1">
                      {language === 'fr' ? 'Paiement' : 'Payment'} #{payment.id?.slice(0, 8) || 'N/A'}
                    </h2>
                    <StatusBadge variant={getStatusVariant(payment.status)} size="lg">
                      {payment.status}
                    </StatusBadge>
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div className="p-4 bg-gradient-to-r from-blue-50/80 to-green-50/80 dark:from-blue-900/20 dark:to-green-900/20 rounded-xl backdrop-blur-sm">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {language === 'fr' ? 'Montant' : 'Amount'}
                </p>
                <p className="text-3xl font-black text-gray-900 dark:text-white">
                  {formatCurrency(payment.amount || 0, payment.currency || 'XAF')}
                </p>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card variant="outlined" className="p-4">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {language === 'fr' ? 'Méthode de Paiement' : 'Payment Method'}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {getPaymentMethodName(payment.paymentMethod || payment.method)}
                  </p>
                </Card>
                {payment.transactionId && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Transaction' : 'Transaction'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      #{payment.transactionId?.slice(0, 8) || 'N/A'}
                    </p>
                  </Card>
                )}
                {payment.createdAt && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Créé le' : 'Created At'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatDate(payment.createdAt)}
                    </p>
                  </Card>
                )}
                {payment.completedAt && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Complété le' : 'Completed At'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatDate(payment.completedAt)}
                    </p>
                  </Card>
                )}
                {payment.gatewayReference && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Référence Gateway' : 'Gateway Reference'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {payment.gatewayReference}
                    </p>
                  </Card>
                )}
                {payment.failureReason && (
                  <Card variant="outlined" className="p-4 md:col-span-2">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Raison de l\'Échec' : 'Failure Reason'}
                    </p>
                    <p className="text-sm font-bold text-red-600 dark:text-red-400">
                      {payment.failureReason}
                    </p>
                  </Card>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Actions Sidebar */}
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          <Card variant="glass" className="backdrop-blur-xl border-white/20 dark:border-white/10">
            <h3 className="font-black text-gray-900 dark:text-white mb-4">
              {language === 'fr' ? 'Actions Admin' : 'Admin Actions'}
            </h3>
            <div className="space-y-3">
              {canRetry && (
                <Button
                  variant="gradient"
                  onClick={() => {
                    setRetryData({});
                    setShowRetryModal(true);
                  }}
                  className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 shadow-xl hover:shadow-2xl transition-all"
                >
                  🔄 {language === 'fr' ? 'Réessayer' : 'Retry'}
                </Button>
              )}
              {canRefund && (
                <Button
                  variant="gradient"
                  onClick={() => {
                    setRefundData({ reason: '', notes: '' });
                    setShowRefundModal(true);
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 shadow-xl hover:shadow-2xl transition-all"
                >
                  ↩️ {language === 'fr' ? 'Rembourser' : 'Refund'}
                </Button>
              )}
              {payment.transactionId && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/admin/transactions/${payment.transactionId}`)}
                  className="w-full"
                >
                  👁️ {language === 'fr' ? 'Voir Transaction' : 'View Transaction'}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Retry Modal */}
      <Modal
        isOpen={showRetryModal}
        onClose={() => {
          setShowRetryModal(false);
          setRetryData({});
        }}
        title={language === 'fr' ? 'Réessayer le Paiement' : 'Retry Payment'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {language === 'fr'
                ? `Paiement: ${formatCurrency(payment?.amount || 0, payment?.currency || 'XAF')}`
                : `Payment: ${formatCurrency(payment?.amount || 0, payment?.currency || 'XAF')}`}
            </p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {language === 'fr'
              ? 'Voulez-vous réessayer ce paiement?'
              : 'Do you want to retry this payment?'}
          </p>
          <div className="flex gap-2 pt-4">
            <Button
              variant="gradient"
              onClick={handleRetry}
              loading={loading}
              className="flex-1 bg-gradient-to-r from-yellow-600 to-orange-600 shadow-xl hover:shadow-2xl transition-all"
            >
              🔄 {language === 'fr' ? 'Réessayer' : 'Retry'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowRetryModal(false);
                setRetryData({});
              }}
              className="flex-1"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Refund Modal */}
      <Modal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundData({ reason: '', notes: '' });
        }}
        title={language === 'fr' ? 'Rembourser le Paiement' : 'Refund Payment'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {language === 'fr'
                ? `Paiement: ${formatCurrency(payment?.amount || 0, payment?.currency || 'XAF')}`
                : `Payment: ${formatCurrency(payment?.amount || 0, payment?.currency || 'XAF')}`}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'fr' ? 'Raison du Remboursement (requis, min 10 caractères)' : 'Refund Reason (required, min 10 chars)'}
            </label>
            <Textarea
              value={refundData.reason}
              onChange={(e) => setRefundData((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder={language === 'fr' ? 'Expliquez la raison du remboursement...' : 'Explain the reason for refund...'}
              rows={4}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'fr' ? 'Notes (optionnel)' : 'Notes (optional)'}
            </label>
            <Textarea
              value={refundData.notes}
              onChange={(e) => setRefundData((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder={language === 'fr' ? 'Notes supplémentaires...' : 'Additional notes...'}
              rows={3}
            />
          </div>
          <div className="flex gap-2 pt-4">
            <Button
              variant="gradient"
              onClick={handleRefund}
              loading={loading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 shadow-xl hover:shadow-2xl transition-all"
            >
              ↩️ {language === 'fr' ? 'Rembourser' : 'Refund'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowRefundModal(false);
                setRefundData({ reason: '', notes: '' });
              }}
              className="flex-1"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default AdminPaymentDetail;
